import React, { useCallback, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { ModelInfo } from "build-ts/metadata";
import { getModelById } from "services/DataModelService";
import { useConfigurationContext } from "context/ConfigurationContext";
import MetricListComponent from "modules/metric/components/MetricList";
import { AggregationType } from "build-ts/enums";
import { ObservationColumnProto } from "build-ts/metadata";

interface MetricListProps {
  onReloadKnowledgeGraph?: () => void;
  splitDetailOpen: boolean;
}

const MetricList: React.FC<MetricListProps> = ({ onReloadKnowledgeGraph, splitDetailOpen }) => {
  const { setSelectedMetric } = useConfigurationContext();
  const createMetricModal = useDisclosure();

  const [createMetricFlag, setCreateMetricFlag] = useState(false);
  const [modelInfoData, setModelInfoData] = useState<ModelInfo | null>(null);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedColumnId, setSelectedColumnId] = useState("");
  const [openCreateMetricForm, setOpenCreateMetricForm] = useState(false);

  const handleSelectModel = useCallback(async (id: string) => {
    if (!id) return;
    const data = await getModelById(id);
    if (data) setModelInfoData(data);
    // Column must be chosen before the form opens
    setSelectedColumnId("");
    setOpenCreateMetricForm(false);
  }, []);

  const handleSelectedModelIdChange = useCallback(
    (id: string) => {
      setSelectedModelId(id);
      handleSelectModel(id);
    },
    [handleSelectModel]
  );

  const handleSelectedColumnIdChange = useCallback((id: string) => {
    setSelectedColumnId(id);
    setOpenCreateMetricForm(!!id);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setSelectedMetric(null as any);
    setCreateMetricFlag(true);
    createMetricModal.onOpen();
  }, [setSelectedMetric, createMetricModal]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateMetricFlag(false);
    setOpenCreateMetricForm(false);
    setSelectedModelId("");
    setSelectedColumnId("");
    createMetricModal.onClose();
  }, [createMetricModal]);

  const defaultObservationColumn: ObservationColumnProto | undefined = selectedColumnId
    ? ObservationColumnProto.create({
        logical_column_header: modelInfoData?.columns.find(c => c.header?.id === selectedColumnId)?.header,
        aggregation:
          modelInfoData?.columns.find(c => c.header?.id === selectedColumnId)?.default_aggregation ??
          AggregationType.SUM,
      })
    : undefined;

  return (
    <MetricListComponent
      splitDetailOpen={splitDetailOpen}
      onReloadKnowledgeGraph={onReloadKnowledgeGraph}
      createMetricFlag={createMetricFlag}
      modelInfoData={modelInfoData}
      selectedModelId={selectedModelId}
      selectedColumnId={selectedColumnId}
      openCreateMetricForm={openCreateMetricForm}
      isCreateModalOpen={createMetricModal.isOpen}
      onOpenCreateModal={handleOpenCreateModal}
      onCloseCreateModal={handleCloseCreateModal}
      onSelectedModelIdChange={handleSelectedModelIdChange}
      onSelectedColumnIdChange={handleSelectedColumnIdChange}
      defaultObservationColumn={defaultObservationColumn}
    />
  );
};

export default MetricList;
